import Section from '../shared/Section';
import Field from '../shared/Field';
import Input from '../shared/Input';
import Select from '../shared/Select';
import Textarea from '../shared/Textarea';
import Grid from '../shared/Grid';
import Btn from '../shared/Btn';
import PatientPicker from '../shared/PatientPicker';
import posCodes from '../../../data/posCodes.json';
import { suggestPatientAccountNumber } from '../../../utils/patientAccountNumber';

const POS_OPTIONS = posCodes.map((/** @type {any} */ p) => ({ value: p.code, label: `${p.code} – ${p.label}` }));
const CLAIM_MODE_OPTIONS = [
  { value: '1', label: 'New' }, { value: '7', label: 'Corrected' }, { value: '8', label: 'Rejected' },
];

/**
 * @param {{
 *   claim: any,
 *   setField: (path: string, value: any) => void,
 *   setMany: (updates: any) => void,
 *   selectPatient: (patient: any) => void,
 *   onCreateNew: () => void,
 *   editable: boolean,
 *   errorsByField: Map<string, any>,
 * }} props
 */
export default function PatientBasicsSection({
  claim, setField, setMany, selectPatient, onCreateNew, editable, errorsByField,
}) {
  // Persist the visible '11' default for place_of_service the first time the
  // user touches the form (creating a service line via DoS counts as a touch).
  // The Select shows '11' even when claim.place_of_service is empty, so the
  // validator's "place_of_service is required" error is otherwise dangling.
  const showCorrected = claim.claim_frequency_code === '7' || claim.claim_frequency_code === '8';
  const isSelf = (claim.patient_relationship_to_insured || '18') === '18';

  // Optum can provide claim-level dates before canonical line items exist.
  // Display that value as the fallback, then write user edits through line[0].
  const firstLine = claim.service_lines?.[0] || {};
  const dateOfService = firstLine.service_date_from || claim.service_date_from || '';
  /** @param {string} v */
  const setDateOfService = (v) => {
    const updates = /** @type {Record<string, any>} */ ({});
    if (!claim.place_of_service) updates.place_of_service = '11';
    const lines = Array.isArray(claim.service_lines) ? [...claim.service_lines] : [];
    if (lines.length === 0) {
      // Match ServiceLineGrid.emptyLine() shape so a stub line is valid enough
      // to round-trip through the wire (POS, units) and survive validation.
      lines.push({
        line_number: 1,
        service_date_from: v, service_date_to: v,
        place_of_service: claim.place_of_service || '11',
        cpt_hcpcs_code: '',
        modifier_1: '', modifier_2: '', modifier_3: '', modifier_4: '',
        diagnosis_pointer_1: null, diagnosis_pointer_2: null,
        diagnosis_pointer_3: null, diagnosis_pointer_4: null,
        charge_amount: '', units: '1', rendering_provider_npi: '',
      });
    } else {
      lines[0] = { ...lines[0], service_date_from: v, service_date_to: v };
    }
    updates.service_lines = lines;
    setMany(updates);
  };

  return (
    <Section
      icon="user"
      title="Patient & Claim Basics"
      subtitle="Who this claim is for and the visit details"
    >
      <Field label="Patient" required error={errorsByField?.get('patient_id')?.message}>
        <PatientPicker
          value={claim.patient_id}
          patientSnapshot={claim.patient}
          onSelect={(/** @type {any} */ p) => p ? selectPatient(p) : setMany({ patient_id: null, patient: null })}
          onCreateNew={onCreateNew}
          disabled={!editable}
        />
      </Field>
      <Grid cols={4}>
        <Field label="Claim Type" required>
          <Select
            value={claim.claim_frequency_code || '1'}
            options={CLAIM_MODE_OPTIONS}
            onChange={(/** @type {any} */ v) => setField('claim_frequency_code', v)}
            disabled={!editable}
          />
        </Field>
        <Field label="Date of Service" required>
          <Input
            data-field="service_lines[1].service_date_from"
            type="date"
            value={dateOfService}
            onChange={(/** @type {any} */ e) => setDateOfService(e.target.value)}
            disabled={!editable}
          />
        </Field>
        <Field label="Place of Service" required>
          <Select
            value={claim.place_of_service || '11'}
            options={POS_OPTIONS}
            onChange={(/** @type {any} */ v) => setField('place_of_service', v)}
            disabled={!editable}
          />
        </Field>
        <Field
          label="Patient Account #"
          caption={
            claim.patient_id && !claim.patient_account_number && editable
              ? 'No account # on file for this patient — Generate one below.'
              : 'Box 26'
          }
        >
          <div style={{ display: 'flex', gap: 6 }}>
            <Input
              data-field="patient_account_number"
              value={claim.patient_account_number || ''}
              onChange={(/** @type {any} */ e) => setField('patient_account_number', e.target.value)}
              disabled={!editable}
            />
            {editable && claim.patient_id && !claim.patient_account_number && (
              <Btn
                kind="ghost"
                size="sm"
                icon="sparkles"
                type="button"
                onClick={() => setField(
                  'patient_account_number',
                  suggestPatientAccountNumber(claim.patient || {}),
                )}
              >
                Generate
              </Btn>
            )}
          </div>
        </Field>
        <Field label="Patient Phone">
          <Input
            data-field="patient.phone"
            value={claim.patient?.phone || ''}
            onChange={(/** @type {any} */ e) => setField('patient.phone', e.target.value)}
            disabled={!editable || isSelf}
          />
        </Field>
      </Grid>
      {showCorrected && (
        <Grid cols={2}>
          <Field label="Original Claim Number" required caption="Required for corrected/resubmitted">
            <Input
              data-field="original_reference_number"
              value={claim.original_reference_number || ''}
              onChange={(/** @type {any} */ e) => setField('original_reference_number', e.target.value)}
              disabled={!editable}
            />
          </Field>
          <Field label="Correction Notes">
            <Textarea
              data-field="notes"
              value={claim.notes || ''}
              onChange={(/** @type {any} */ e) => setField('notes', e.target.value)}
              disabled={!editable}
              rows={2}
              placeholder="Reason for correction"
            />
          </Field>
        </Grid>
      )}
    </Section>
  );
}
